import {z} from 'zod';

import i18n from '@dms/i18n';

const DISCOUNT_VALUE_MIN = 0;
const DISCOUNT_VALUE_MAX = 100;

const VEHICLE_AGE_TYPES = ['VEHICLE_AGE_UNDECIDED', 'VEHICLE_AGE_IN_MONTHS'] as const;

const LABOUR_DISCOUNT_TYPES = ['DISCOUNTED_FIXED_RATE', 'PERCENTAGE_DISCOUNT'] as const;

const MATERIAL_DISCOUNT_TYPES = [
  'DISCOUNT_CUSTOMER_GROUP',
  'DISCOUNT_SALE_PRICE',
  'DISCOUNT_MARGIN_PURCHASE_PRICE',
] as const;

const ALL_DISCOUNT_TYPES = [...LABOUR_DISCOUNT_TYPES, ...MATERIAL_DISCOUNT_TYPES] as const;

export const discountFormSchema = z
  .object({
    name: z.string().trim().min(1, i18n.t('general.validations.fieldIsRequired')),
    vehicleType: z.string().nullish(),
    vehicleMake: z.string().nullish(),
    vehicleModelFamilies: z.array(z.string().nullable()).optional(),
    vehicleModels: z.array(z.string().nullable()).optional(),
    vehicleAgeType: z.enum(VEHICLE_AGE_TYPES),
    vehicleAgeFrom: z.number().positive().nullish(),
    vehicleAgeTo: z.number().positive().nullish(),
    workTypes: z.array(z.string().nullable()).optional(),
    isServiceOrderVariant: z.boolean().nullish(),
    serviceOrderVariantIds: z.array(z.string().nullable()).optional(),
    isDirectSaleVariant: z.boolean().nullish(),
    directSaleVariantIds: z.array(z.string().nullable()).optional(),
    sparePartManufacturers: z.array(z.string().nullable()).nullish(),
    discountSettingsType: z.enum(ALL_DISCOUNT_TYPES),
    discountSettingsValue: z.number(),
  })
  .superRefine((data, ctx) => {
    if (!data.isServiceOrderVariant && !data.isDirectSaleVariant) {
      const message = i18n.t('entity.customerContract.labels.variantValidation');

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['isServiceOrderVariant'],
        message,
      });

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['isDirectSaleVariant'],
        message,
      });
    }

    if (data.discountSettingsValue != null) {
      if (data.discountSettingsValue < DISCOUNT_VALUE_MIN) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: DISCOUNT_VALUE_MIN,
          type: 'number',
          inclusive: true,
          path: ['discountSettingsValue'],
        });
      }

      const hasPercentageMax = data.discountSettingsType !== 'DISCOUNTED_FIXED_RATE';

      if (hasPercentageMax && data.discountSettingsValue > DISCOUNT_VALUE_MAX) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_big,
          maximum: DISCOUNT_VALUE_MAX,
          type: 'number',
          inclusive: true,
          path: ['discountSettingsValue'],
        });
      }
    }
  });

export type DiscountFormType = z.infer<typeof discountFormSchema>;
